import { useCallback, useMemo, useState } from "react";

import Card from "ds/components/Card";
import Typography from "ds/components/Typography";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import { ConfettiColored } from "components/icons/generated";
import Icon from "ds/components/Icon";
import ProgressBar from "ds/components/ProgressBar";
import TextEllipsis from "ds/components/TextEllipsis";
import useUserGuideProgressMutations from "shared/UserGuides/useUserGuideProgressMutations";
import { CompletedGuideData } from "components/AssistantPanel/Learn/types";
import { AnalyticsPageUserGuides } from "hooks/useAnalytics/pages/userGuides";
import {
  getHardcodedUserGuideGroupIconAndColor,
  randomIconAndColor,
} from "shared/UserGuides/mocks";

import useAssistantPanelLearnStore from "../../../useStore";
import styles from "./styles.module.css";

type AssistantPanelLearnActiveGuideViewCompletionStepProps = {
  data: CompletedGuideData;
};

const AssistantPanelLearnActiveGuideViewCompletionStep = ({
  data,
}: AssistantPanelLearnActiveGuideViewCompletionStepProps) => {
  const { setNavigationState, navigationState } = useAssistantPanelLearnStore();
  const { startUserGuide, restartUserGuide } = useUserGuideProgressMutations();

  const [isStartingNextGuide, setIsStartingNextGuide] = useState(false);

  const handleFinish = useCallback(() => {
    if (navigationState.level === "guide") {
      setNavigationState({ level: "chapters", groupId: navigationState.groupId });
    } else {
      setNavigationState({ level: "groups" });
    }
  }, [setNavigationState, navigationState]);

  const handleStartNextGuide = useCallback(async () => {
    if (!data.nextGuideId) return;

    setIsStartingNextGuide(true);
    try {
      if (data.isNextGuideCompleted) {
        await restartUserGuide({ id: data.nextGuideId });
      } else {
        await startUserGuide({ id: data.nextGuideId });
      }
    } finally {
      setIsStartingNextGuide(false);
    }
  }, [data.nextGuideId, data.isNextGuideCompleted, startUserGuide, restartUserGuide]);

  const icon = useMemo(() => {
    if (navigationState.level === "guide" || navigationState.level === "chapters") {
      const { groupId } = navigationState;
      if (groupId) {
        return getHardcodedUserGuideGroupIconAndColor(groupId).icon;
      }
    }

    // if from some reason we are in the groups level, fallback to a random icon and color
    return randomIconAndColor().icon;
  }, [navigationState]);

  return (
    <Box direction="column" gap="large" grow="1" padding="x-large" scrollable>
      <Box align="center" justify="between" fullWidth gap="medium">
        <Box align="center" gap="small" className={styles.guideTitle} padding="small">
          <Icon src={icon} />
          <TextEllipsis tooltip={data.guideTitle} tooltipWidthMode="maxWidthXl">
            {(props) => (
              <Typography {...props} tag="span" variant="p-body4">
                {data.guideTitle}
              </Typography>
            )}
          </TextEllipsis>
        </Box>

        <Box grow="1" align="center" gap="medium" className={styles.progressBar}>
          <ProgressBar
            current={data.totalSteps}
            max={data.totalSteps}
            fullWidth
            ariaLabel="Guide progress"
          />
        </Box>
      </Box>

      <Card contentGap="large" className={styles.card}>
        <Icon src={ConfettiColored} size="x-large" />
        <Typography tag="div" variant="p-body3">
          <MarkdownAssistantPanel>{data.completionMessage}</MarkdownAssistantPanel>
        </Typography>

        <Box direction="row" gap="medium" align="center">
          <Button
            variant={data.nextGuideId ? "secondary" : "primary"}
            size="small"
            onPress={handleFinish}
            disabled={isStartingNextGuide}
            analyticsPage={AnalyticsPageUserGuides.UserGuides}
            analyticsTitle="Finish Guide Clicked"
            analyticsProps={{
              button: "Finish",
              userGuideName: data.guideTitle,
            }}
          >
            Finish
          </Button>
          {data.nextGuideId && (
            <Button
              variant="primary"
              size="small"
              onPress={handleStartNextGuide}
              disabled={isStartingNextGuide}
              loading={isStartingNextGuide}
              analyticsPage={AnalyticsPageUserGuides.UserGuides}
              analyticsTitle="Finish Guide Clicked"
              analyticsProps={{
                button: "Next mission",
                userGuideName: data.guideTitle,
              }}
            >
              Next mission
            </Button>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default AssistantPanelLearnActiveGuideViewCompletionStep;
